// PayrollPage — 1:1 port of admin/views/payroll.php.
// Same layout: Generate-Payroll filter bar on top, then a Payroll Records table.
// Generate / View / Delete actions map to the three /api/payroll routes.

import React, { useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, EmptyState, Skeleton } from "../../Components/ui";

const inputStyle = {
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 13,
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
};
const btnPrimary = {
  background: "var(--accent-primary)",
  color: "white",
  padding: "8px 16px",
  border: "none",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};
const btnGhost = {
  background: "transparent",
  color: "var(--text-secondary)",
  padding: "6px 12px",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  fontSize: 13,
  cursor: "pointer",
};

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function lastOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}
function inr(n) {
  return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPeriod(a, b) {
  const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");
  return `${fmt(a)} → ${fmt(b)}`;
}

export default function PayrollPage() {
  const toast = useToast();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useShellHeader({ title: "Payroll", breadcrumb: "Admin · Payroll" }, []);

  async function load() {
    if (!from || !to) {
      toast({ title: "Please provide both start and end dates.", status: "warning" });
      return;
    }
    setLoading(true);
    try {
      const data = await api(`/payroll?periodStart=${from}&periodEnd=${to}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function generate() {
    if (!from || !to) {
      toast({ title: "Please provide both start and end dates.", status: "warning" });
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const r = await api("/payroll/generate", {
        method: "POST",
        body: JSON.stringify({ periodStart: from, periodEnd: to }),
      });
      setMsg(r.message || `Payroll generated for ${r.count} staff members.`);
      toast({ title: r.message, status: "success" });
      await load();
    } catch (e) {
      toast({ title: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this payroll record?")) return;
    try {
      await api(`/payroll/${id}`, { method: "DELETE" });
      toast({ title: "Payroll record deleted.", status: "success" });
      load();
    } catch (e) {
      toast({ title: e.message, status: "error" });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <CardHeader title="Generate Payroll" subtitle="Calculates net pay per active staff for the chosen period." />
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: 16 }}>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>Period Start:</label>
          <input type="date" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>Period End:</label>
          <input type="date" style={inputStyle} value={to} onChange={(e) => setTo(e.target.value)} />
          <button style={btnPrimary} onClick={generate} disabled={loading}>Generate</button>
          <button style={btnGhost} onClick={load} disabled={loading}>View</button>
        </div>
        {msg && (
          <div style={{ padding: "0 16px 16px", color: "var(--text-secondary)", fontSize: 13 }}>{msg}</div>
        )}
      </Card>

      <Card>
        <CardHeader title="Payroll Records" subtitle={rows.length ? `${rows.length} record(s)` : "Set a period and click \"View\" to load payroll."} />
        {loading ? (
          <Skeleton lines={4} />
        ) : rows.length === 0 ? (
          <EmptyState title="No payroll records" message='Set a period and click "View" to load payroll, or click "Generate" to create records for the period.' />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                  {["#", "Staff", "Period", "Base Salary (₹)", "Deductions (₹)", "Bonuses (₹)", "Net Pay (₹)", "Status", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontWeight: 600, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-default)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const s = r.staff || {};
                  const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.email || "—";
                  return (
                    <tr key={r._id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <td style={{ padding: "10px 12px" }}>{i + 1}</td>
                      <td style={{ padding: "10px 12px" }}>{name}</td>
                      <td style={{ padding: "10px 12px" }}>{fmtPeriod(r.periodStart, r.periodEnd)}</td>
                      <td style={{ padding: "10px 12px" }}>{inr(r.baseSalary)}</td>
                      <td style={{ padding: "10px 12px" }}>{inr(r.deductions)}</td>
                      <td style={{ padding: "10px 12px" }}>{inr(r.bonuses)}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 600 }}>{inr(r.netPay)}</td>
                      <td style={{ padding: "10px 12px", textTransform: "capitalize" }}>{r.status}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <button style={{ ...btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => remove(r._id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
